import { useCallback, useEffect, useMemo, useState } from 'react';

import { Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table } from 'react-bootstrap';
import { useHistory, useParams } from 'react-router-dom';
import { FaArrowLeft, FaPlay } from 'react-icons/fa6';
import { useApp } from 'common/AppContext';

import { unwrap } from './helpers';

const STATUS_META = {
    1: { label: 'Passed', variant: 'success' },
    2: { label: 'Blocked', variant: 'secondary' },
    3: { label: 'Untested', variant: 'light' },
    4: { label: 'Retest', variant: 'warning' },
    5: { label: 'Failed', variant: 'danger' }
};

const PRIORITY_VARIANT = {
    1: 'secondary',
    2: 'info',
    3: 'warning',
    4: 'danger'
};

/**
 * Flatten a test (or case)'s steps to plain text so the runner can use the
 * brief verbatim. Mirrors the helper in CaseDetail.js — kept inline here so
 * RunDetail is self-contained.
 */
function flattenStepsToText(test) {

    if (Array.isArray(test.custom_steps_separated) && test.custom_steps_separated.length)
        return test.custom_steps_separated.map((s, i) => {

            const action = (s.content || '').trim();
            const expected = (s.expected || '').trim();
            return `${i + 1}. ${action}${expected ? `\n   Expected: ${expected}` : ''}`;

        }).join('\n');

    return (test.custom_steps || '').trim();

}

export const RunDetail = () => {

    const { api } = useApp();
    const { id } = useParams();
    const history = useHistory();

    const [run, setRun] = useState(null);
    const [tests, setTests] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [priorities, setPriorities] = useState([]);
    const [apps, setApps] = useState([]);
    const [selectedEnvId, setSelectedEnvId] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const load = useCallback(async () => {

        setLoading(true);
        setError(null);

        try {

            const [runData, testsData] = await Promise.all([
                api.get(`/api/testrail/runs/${id}`),
                api.get(`/api/testrail/runs/${id}/tests`)
            ]);
            setRun(runData);
            setTests(unwrap(testsData, 'tests'));

        } catch (e) {

            setError(e?.err || e?.message || String(e));

        } finally {

            setLoading(false);

        }

    }, [api, id]);

    useEffect(() => {

        load();

    }, [load]);

    useEffect(() => {

        api.get('/api/testrail/case-types').then(d => setCaseTypes(unwrap(d, 'case_types'))).catch(() => {});
        api.get('/api/testrail/priorities').then(d => setPriorities(unwrap(d, 'priorities'))).catch(() => {});
        api.get('/api/apps').then(d => setApps(d?.apps || [])).catch(() => {});

    }, [api]);

    const typeName = useMemo(
        () => Object.fromEntries(caseTypes.map(t => [t.id, t.name])),
        [caseTypes]
    );
    const priorityName = useMemo(
        () => Object.fromEntries(priorities.map(p => [p.id, p.name])),
        [priorities]
    );

    // Match the run's TestRail project to a registered App (admins set
    // testrail_project_id on Admin → Apps). When matched, narrow the env
    // dropdown to that app's environments and pre-select the first one;
    // when no match, fall back to every registered environment.
    const matchedApp = useMemo(
        () => (run && run.project_id)
            ? apps.find(a => a.testrail_project_id === run.project_id) || null
            : null,
        [apps, run]
    );

    const environments = useMemo(() => {

        if (matchedApp) return (matchedApp.environments || []).map(e => ({ ...e, app_id: matchedApp.id }));
        return apps.flatMap(a => (a.environments || []).map(e => ({ ...e, app_id: a.id, app_name: a.name })));

    }, [apps, matchedApp]);

    useEffect(() => {

        // Auto-select the first env once we've resolved which envs to
        // show. Skip if the user has already chosen one.
        if (!selectedEnvId && environments.length > 0)
            setSelectedEnvId(String(environments[0].id));

    }, [environments, selectedEnvId]);

    const selectedEnv = useMemo(
        () => environments.find(e => String(e.id) === String(selectedEnvId)) || null,
        [environments, selectedEnvId]
    );

    const filteredTests = useMemo(() => {

        if (statusFilter === 'all') return tests;
        const wanted = parseInt(statusFilter, 10);
        return tests.filter(t => t.status_id === wanted);

    }, [tests, statusFilter]);

    const launchTest = test => {

        if (!selectedEnv) return;

        const testPlan = `${test.title}\n\n${flattenStepsToText(test)}`.trim();
        history.push('/feature-tester', {
            prefill: {
                appId: selectedEnv.app_id || null,
                freeform: true,
                source: {
                    id: test.id,
                    case_id: test.case_id,
                    run_id: test.run_id,
                    title: test.title,
                    type: 'testrail_test'
                },
                testPlan,
                url: selectedEnv.url
            }
        });

    };

    if (loading) return (
        <Container className="py-4">
            <Spinner animation="border" />
        </Container>
    );

    if (error && !run)
        return (
            <Container className="py-4">
                <Button className="mb-3" onClick={() => history.push('/testrail/runs')} variant="outline-secondary">
                    <FaArrowLeft className="me-2" />
                    Back to runs
                </Button>
                <Alert variant="danger">
                    {error}
                </Alert>
            </Container>
        );

    return (

        <Container className="py-4">

            <Button className="mb-3" onClick={() => history.push('/testrail/runs')} variant="outline-secondary">
                <FaArrowLeft className="me-2" />
                Back to runs
            </Button>

            {error && (
                <Alert dismissible onClose={() => setError(null)} variant="danger">
                    {error}
                </Alert>
            )}

            <Card className="border-0 shadow-sm mb-3">
                <Card.Body>

                    <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
                        <div>
                            <div className="text-muted small">
                                R
                                {run.id}
                            </div>
                            <h4 className="mb-1">
                                {run.name}
                            </h4>
                            {run.description && (
                                <div className="text-muted small">
                                    {run.description}
                                </div>
                            )}
                        </div>
                        {run.url && (
                            <a className="btn btn-sm btn-outline-secondary" href={run.url} rel="noopener noreferrer" target="_blank">
                                Open in TestRail
                            </a>
                        )}
                    </div>

                    <div className="d-flex flex-wrap gap-2 mb-3">
                        <Badge bg="success">
                            Passed:
                            {' '}
                            {run.passed_count || 0}
                        </Badge>
                        <Badge bg="danger">
                            Failed:
                            {' '}
                            {run.failed_count || 0}
                        </Badge>
                        <Badge bg="warning" text="dark">
                            Retest:
                            {' '}
                            {run.retest_count || 0}
                        </Badge>
                        <Badge bg="secondary">
                            Blocked:
                            {' '}
                            {run.blocked_count || 0}
                        </Badge>
                        <Badge bg="light" text="dark">
                            Untested:
                            {' '}
                            {run.untested_count || 0}
                        </Badge>
                    </div>

                    <Row className="g-2">
                        <Col md={7}>
                            <Form.Group>
                                <Form.Label className="small mb-1">
                                    Environment for Feature Tester runs
                                    {matchedApp && (
                                        <span className="text-muted ms-2" style={{ fontSize: 11 }}>
                                            (linked to
                                            {' '}
                                            <strong className="text-body">
                                                {matchedApp.name}
                                            </strong>
                                            )
                                        </span>
                                    )}
                                </Form.Label>
                                {environments.length > 0 ? (
                                    <Form.Select onChange={e => setSelectedEnvId(e.target.value)} value={selectedEnvId}>
                                        <option value="">
                                            Select an environment…
                                        </option>
                                        {environments.map(env => (
                                            <option key={env.id} value={env.id}>
                                                {env.name}
                                                {' — '}
                                                {env.url}
                                                {!matchedApp && env.app_name && ` · ${env.app_name}`}
                                            </option>
                                        ))}
                                    </Form.Select>
                                ) : (
                                    <Alert className="small mb-0" variant="warning">
                                        No environments registered. Add one under Admin → Apps.
                                    </Alert>
                                )}
                            </Form.Group>
                        </Col>
                        <Col md={5}>
                            <Form.Group>
                                <Form.Label className="small mb-1">
                                    Filter by status
                                </Form.Label>
                                <Form.Select onChange={e => setStatusFilter(e.target.value)} value={statusFilter}>
                                    <option value="all">
                                        All statuses
                                    </option>
                                    <option value="3">
                                        Untested only
                                    </option>
                                    <option value="5">
                                        Failed only
                                    </option>
                                    <option value="4">
                                        Retest only
                                    </option>
                                    <option value="1">
                                        Passed only
                                    </option>
                                    <option value="2">
                                        Blocked only
                                    </option>
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </Row>

                </Card.Body>
            </Card>

            {filteredTests.length === 0 ? (
                <Alert variant="light">
                    {statusFilter === 'all' ? 'This run has no tests.' : 'No tests match this filter.'}
                </Alert>
            ) : (
                <Card className="border-0 shadow-sm">
                    <Card.Body className="p-0">
                        <Table hover responsive className="m-0">
                            <thead>
                                <tr>
                                    <th>
                                        Case
                                    </th>
                                    <th>
                                        Title
                                    </th>
                                    <th>
                                        Type
                                    </th>
                                    <th>
                                        Priority
                                    </th>
                                    <th>
                                        Status
                                    </th>
                                    <th className="text-end">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredTests.map(t => {

                                    const status = STATUS_META[t.status_id] || { label: `#${t.status_id}`, variant: 'secondary' };
                                    return (
                                        <tr key={t.id}>
                                            <td className="text-muted small align-middle">
                                                C
                                                {t.case_id}
                                            </td>
                                            <td className="align-middle">
                                                {t.title}
                                            </td>
                                            <td className="align-middle">
                                                {t.type_id && (
                                                    <span className="text-muted small">
                                                        {typeName[t.type_id] || `#${t.type_id}`}
                                                    </span>
                                                )}
                                            </td>
                                            <td className="align-middle">
                                                {t.priority_id && (
                                                    <Badge bg={PRIORITY_VARIANT[t.priority_id] || 'secondary'}>
                                                        {priorityName[t.priority_id] || `P${t.priority_id}`}
                                                    </Badge>
                                                )}
                                            </td>
                                            <td className="align-middle">
                                                <Badge bg={status.variant} text={status.variant === 'light' ? 'dark' : undefined}>
                                                    {status.label}
                                                </Badge>
                                            </td>
                                            <td className="text-end align-middle">
                                                <div className="d-flex gap-2 justify-content-end">
                                                    <Button
                                                        onClick={() => history.push(`/testrail/cases/${t.case_id}`)}
                                                        size="sm"
                                                        variant="outline-secondary"
                                                    >
                                                        Open case
                                                    </Button>
                                                    <Button
                                                        disabled={!selectedEnv}
                                                        onClick={() => launchTest(t)}
                                                        size="sm"
                                                        title={!selectedEnv ? 'Pick an environment first' : 'Launch in Feature Tester'}
                                                        variant="success"
                                                    >
                                                        <FaPlay className="me-1" />
                                                        Run
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    );

                                })}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            )}

        </Container>

    );

};
